import type { IconName } from 'hooks/usePreferences/allTools';
import customIcons from 'hooks/usePreferences/customIcons';
import textSizes from 'styles/sizes';
import Adjust from '@mui/icons-material/Adjust';
import Block from '@mui/icons-material/Block';
import Bookmark from '@mui/icons-material/Bookmark';
import BorderColor from '@mui/icons-material/BorderColor';
import BorderHorizontal from '@mui/icons-material/BorderHorizontal';
import BorderVertical from '@mui/icons-material/BorderVertical';
import Cached from '@mui/icons-material/Cached';
import CallMade from '@mui/icons-material/CallMade';
import Check from '@mui/icons-material/Check';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Code from '@mui/icons-material/Code';
import CropSquare from '@mui/icons-material/CropSquare';
import Clear from '@mui/icons-material/Clear';
import Delete from '@mui/icons-material/Delete';
import Error from '@mui/icons-material/Error';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import Exposure from '@mui/icons-material/Exposure';
import FiberManualRecord from '@mui/icons-material/FiberManualRecord';
import FileCopyRounded from '@mui/icons-material/FileCopyRounded';
import FindInPage from '@mui/icons-material/FindInPage';
import FontDownload from '@mui/icons-material/FontDownload';
import GpsNotFixed from '@mui/icons-material/GpsNotFixed';
import HelpOutlineTwoTone from '@mui/icons-material/HelpOutlineTwoTone';
import InvertColors from '@mui/icons-material/InvertColors';
import Layers from '@mui/icons-material/Layers';
import Navigation from '@mui/icons-material/Navigation';
import PanoramaFishEye from '@mui/icons-material/PanoramaFishEye';
import PanTool from '@mui/icons-material/PanTool';
import PlayArrow from '@mui/icons-material/PlayArrow';
import PriorityHigh from '@mui/icons-material/PriorityHigh';
import Refresh from '@mui/icons-material/Refresh';
import Save from '@mui/icons-material/Save';
import Share from '@mui/icons-material/Share';
import SkipNext from '@mui/icons-material/SkipNext';
import SkipNextOutlined from '@mui/icons-material/SkipNextOutlined';
import SkipPrevious from '@mui/icons-material/SkipPrevious';
import SkipPreviousOutlined from '@mui/icons-material/SkipPreviousOutlined';
import Straighten from '@mui/icons-material/Straighten';
import SwapCalls from '@mui/icons-material/SwapCalls';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import SwapVert from '@mui/icons-material/SwapVert';
import ThreeDRotation from '@mui/icons-material/ThreeDRotation';
import VerticalSplit from '@mui/icons-material/VerticalSplit';
import Visibility from '@mui/icons-material/Visibility';
import Warning from '@mui/icons-material/Warning';
import ZoomIn from '@mui/icons-material/ZoomIn';
import Assignment from '@mui/icons-material/Assignment';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronLeft from '@mui/icons-material/ChevronLeft';

import { ReactComponent as ViewInArIcon } from 'assets/ViewInArIcon.svg';

import { ReactComponent as FastRoiIcon } from 'assets/FastRoi.svg';
import { ReactComponent as FastRulerIcon } from 'assets/FastRuler.svg';

const sizes = {
  small: '1.6rem',
  large: '2.4rem',
} as const;

export type IconProps = Readonly<{
  size?: 'small' | 'large' | keyof typeof textSizes;
  name: IconName;
  className?: string;
  css?: string;
  svgIcon?: string;
  ['data-testid']?: string;
  style?: React.CSSProperties;
}>;

const getSize = ({ name, size }: { name: string | IconName; size: string }): string => {
  if (size in sizes) {
    // Manually fix sizes of MUI icons
    switch (name) {
      case 'erase':
      case 'pan_tool':
      case 'reset':
      case 'regular_scroll':
        return size === 'large' ? '2rem' : '1.4rem';
      default:
        // @ts-expect-error [EN-10263] - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly small: "1.6rem"; readonly large: "2.4rem"; }'.
        return sizes[size];
    }
  }

  // @ts-expect-error [EN-10263] - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly toolbarHeight: "6rem"; readonly toolbarButtonHeight: "3.2rem"; readonly buttonText: "0.875rem"; readonly toolbarItemSpacing: "1rem"; readonly textSmall: "1.1rem"; readonly textBody1: "1.4rem"; ... 12 more ...; readonly display1: "1.6rem"; }'.
  return textSizes[size];
};

/** Resolves an icon name into an icon SVG element */
const resolveIcon = ({
  name,
  css,
  ...props
}: {
  className: undefined | string;
  css?: string;
  name: IconName;
  ['data-testid']?: string;
  style: {
    fontSize: string;
  };
}) => {
  switch (name) {
    case 'assignment':
      return <Assignment {...props} css={css} />;
    case 'pan_tool':
      return <PanTool {...props} css={css} />;
    case 'zoom_in':
      return <ZoomIn {...props} css={css} />;
    case 'swap_horizontal':
      return <SwapHoriz {...props} css={css} />;
    case 'swap_vertical':
      return <SwapVert {...props} css={css} />;
    case 'exposure':
      return <Exposure {...props} css={css} />;
    case 'straighten':
      return <Straighten {...props} css={css} />;
    case 'share':
      return <Share {...props} css={css} />;
    case 'code':
      return <Code {...props} css={css} />;
    case 'panorama_fish_eye':
      return <PanoramaFishEye {...props} css={css} />;
    case 'adjust':
      return <Adjust {...props} css={css} />;
    case 'invert_colors':
      return <InvertColors {...props} css={css} />;
    case 'cached':
      return <Cached {...props} css={css} />;
    case 'file_copy':
      return <FileCopyRounded {...props} css={css} />;
    case 'find_in_page':
      return <FindInPage {...props} css={css} />;
    case 'crop_square':
      return <CropSquare {...props} css={css} />;
    case 'play_arrow':
      return <PlayArrow {...props} css={css} />;
    case 'delete':
      return <Delete {...props} css={css} />;
    case 'vertical_split':
      return <VerticalSplit {...props} css={css} />;
    case 'location_searching':
      return <GpsNotFixed {...props} css={css} />;
    case 'key_image':
      return <Bookmark {...props} css={css} />;
    case 'layers':
      return <Layers {...props} css={css} />;
    case 'border_color':
      return <BorderColor {...props} css={css} />;
    case 'border_vertical':
      return <BorderVertical {...props} css={css} />;
    case 'border_horizontal':
      return <BorderHorizontal {...props} css={css} />;
    case 'font_download':
      return <FontDownload {...props} css={css} />;
    case '3d_rotation':
      return <ThreeDRotation {...props} css={css} />;
    case 'anatomic_navigator':
      return <Navigation {...props} css={css} />;
    case 'check_circle':
      return <CheckCircle {...props} css={css} />;
    case 'delete_icon':
      return <Delete {...props} css={css} />;
    case 'fiber_manual_record':
      return <FiberManualRecord {...props} css={css} />;
    case 'save':
      return <Save {...props} css={css} />;
    case 'skip_previous':
      return <SkipPrevious {...props} css={css} />;
    case 'skip_next':
      return <SkipNext {...props} css={css} />;
    case 'skip_previous_outlined':
      return <SkipPreviousOutlined {...props} css={css} />;
    case 'skip_next_outlined':
      return <SkipNextOutlined {...props} css={css} />;
    case 'arrow':
      return <CallMade {...props} css={css} />;
    case 'visibility':
      return <Visibility {...props} css={css} />;
    case 'swap_calls':
      return <SwapCalls {...props} css={css} />;
    case 'check':
      return <Check {...props} css={css} />;
    case 'refresh':
      return <Refresh {...props} css={css} />;
    case 'warning':
      return <Warning {...props} css={css} />;
    case 'block':
      return <Block {...props} css={css} />;
    case 'clear':
      return <Clear {...props} css={css} />;
    case 'error':
      return <Error {...props} css={css} />;
    case 'error_outline':
      return <ErrorOutline {...props} css={css} />;
    case 'priority_high':
      return <PriorityHigh {...props} css={css} />;
    case 'switch_viewtype':
      return <ViewInArIcon {...props} css={css} />;
    case 'fast_ruler':
      return <FastRulerIcon {...props} css={css} />;
    case 'fast_roi':
      return <FastRoiIcon {...props} css={css} />;
    case 'toggle_thumbnail_bar':
      return <ExpandLess {...props} css={css} />;
    case 'toggle_tool_bar':
      return <ChevronLeft {...props} css={css} />;
    case 'toggle_cad_annotation':
      return <Visibility {...props} css={css} />;
    case 'expand_vertical_chevron':
      return (
        <ChevronLeft
          {...props}
          style={{ ...props.style, transform: 'rotate(90deg)', verticalAlign: 'middle' }}
        />
      );
    case 'collapse_vertical_chevron':
      return (
        <ChevronLeft
          {...props}
          style={{ ...props.style, transform: 'rotate(-90deg)', verticalAlign: 'middle' }}
        />
      );
    case 'edit_pencil':
      return <BorderColor {...props} style={{ ...props.style, verticalAlign: 'middle' }} />;
    case 'expand_less':
      return <ExpandLess {...props} css={css} />;
    case 'expand_more':
      return <ExpandMore {...props} css={css} />;
    default:
      return <HelpOutlineTwoTone {...props} css={css} />;
  }
};

export const Icon = ({
  size = 'large',
  className,
  name,
  svgIcon,
  'data-testid': testid,
  ...rest
}: IconProps): React.ReactElement => {
  const computedSize = getSize({ name, size });

  if (svgIcon != null && svgIcon.length > 0) {
    return (
      <div
        className={className}
        css={`
          // svgIcon comes from 3rd-parties and may not be 100% safe to inline it
          // we instead use it as base64 image so that it can be safely used in
          // the application without XSS risks
          mask-image: url(${svgIcon});
          width: ${computedSize};
          aspect-ratio: 1;
          background-color: currentColor;
        `}
      />
    );
  }

  // eslint-disable-next-line no-prototype-builtins
  if (customIcons.hasOwnProperty(name)) {
    // @ts-expect-error [EN-10263] - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Partial<Record<CustomIconKey, CustomIcon>>'.
    const { svg, stroke, fontSize } = customIcons[name];
    return (
      <svg
        className={className}
        viewBox="0 0 24 24"
        css={`
          font-size: ${fontSize ?? computedSize};
          stroke: ${stroke};
          width: ${computedSize};
          height: ${computedSize};
          fill: transparent;
          strokewidth: 2;
        `}
      >
        {svg}
      </svg>
    );
  }

  return resolveIcon({
    name,
    className,
    style: {
      fontSize: computedSize,
    } as any,
    'data-testid': testid ?? `icon-${name}`,
    ...rest,
  });
};
